// src/web/glasses.js
//
// MediaPipe-native glasses renderer.
//
// The glasses GLB is the ORIGINAL rayban.glb (materials intact). We apply a
// fixed alignment matrix A (computed offline, verified against the canonical
// face) that maps the model into canonical face space. Per frame, the object
// matrix is set to M · A where M is MediaPipe's transformation matrix.
//
// Because the occluder is posed by M and the glasses by M · A (and A places the
// model correctly relative to the canonical face the occluder represents), the
// two are consistent by construction. There is NO per-face offset, scale, fill,
// rotation, or depth-bias tuning. That entire class of knobs is gone.
//
// Materials: we keep the GLB's own materials and add environment reflections.
// The lens material ('Clear Glass' in this model) gets transmission so it reads
// as tinted glass with cubemap reflections.

import {
  Group, Matrix4, Vector3, Box3, MeshPhysicalMaterial, Color
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { GLASSES_ALIGNMENT_MATRIX } from './canonicalFace.js';
import { maxPenetration } from './canonicalHeightField.js';

export class Glasses {
  constructor({ envMap, lensSpec }) {
    this.envMap = envMap;
    this.lensSpec = lensSpec || {};
    this.root = new Group();
    this.root.matrixAutoUpdate = false;
    this.root.frustumCulled = false;
    this.root.renderOrder = 10;

    this._A = new Matrix4().fromArray(GLASSES_ALIGNMENT_MATRIX);
    this._MA = new Matrix4();
    // Bridge-pinning scratch objects.
    // The model's bridge point in MODEL space. A_shifted maps this to the
    // canonical bridge (plus any nose/tilt/scale). We transform THIS through
    // A_shifted to get p — applying A to the already-canonical bridge would
    // scale it ~98× and collapse the frame.
    this._modelBridge = new Vector3(0, 0, 0);   // set after load from bbox
    // Pin point in CANONICAL space: the canonical SKIN bridge (landmark 168),
    // z nose-warped per user by main.js. The pinned matrix places THIS point on
    // the live landmark, so the frame keeps the exact distance from the skin
    // that auto-seat solved. (Pinning the frame's own front point instead put
    // the front ON the skin, sinking the pads into the nose and shifting the
    // frame toward the far side under yaw.)
    this._pin = new Vector3(0, 3.271, 5.236);
    this._p = new Vector3();
    this._rsP = new Vector3();
    this._Mrs = new Matrix4();
    this._Mpinned = new Matrix4();
    this._noseShift = new Matrix4();   // forward (+Z) shift to seat on the nose
    this._scaleMat = new Matrix4();    // per-model scale multiplier
    this._tiltMat = new Matrix4();     // pantoscopic tilt about the bridge
    this._A_shifted = new Matrix4();   // = noseShift · tilt · scale · A
    // ?nose= / ?scale= / ?tilt= override every model's values (live tuning).
    const qs = new URLSearchParams(window.location.search);
    const ns = parseFloat(qs.get('nose'));
    const sc = parseFloat(qs.get('scale'));
    const tl = parseFloat(qs.get('tilt'));
    this._noseOverride = Number.isFinite(ns) ? ns : null;
    this._scaleOverride = Number.isFinite(sc) ? sc : null;
    this._tiltOverride = Number.isFinite(tl) ? tl : null;
    this._noseShiftZ = this._noseOverride ?? 0.05;
    // Automatic seating (see autoSeat): forward shift solved from the model's
    // own geometry so its front never sits inside the canonical face. The
    // per-model `noseDistance` from the catalog is then a manual OFFSET on top.
    this._autoSeatOn = !qs.has('noautoseat') && this._noseOverride === null;
    this._autoShiftZ = 0;
    this._noseOffsetZ = 0;
    // True-to-scale: when the pose matrix carries the user's head scale s (see
    // PoseSolver.setScale), the frame must NOT grow with the head — real frames
    // have a fixed size. We divide the frame's canonical-space scale by s so
    // s · (1/s) = 1 in world units; auto-seat is re-solved in that space.
    this._headScale = 1;
    // Temple splay: arms rotated outward about their hinges to the user's head
    // width (see core/headWidth.js). Ratio 1 = canonical. ?temples=off disables.
    this._splayRatio = 1;
    this._splayOn = (qs.get('temples') || 'on').toLowerCase() !== 'off';
    this._frameStats = null;   // { frontZ, halfWidth, minZ } in canonical space
    this._scale = this._scaleOverride ?? 1.0;
    this._pantoTiltDeg = this._tiltOverride ?? 0.0;
    this._rebuildShift();
    this._model = null;
    this._lensMats = [];
    this._frameMats = [];
    this._loader = new GLTFLoader();
  }

  _rebuildShift() {
    // Compose in CANONICAL space, applied AFTER A. Order:
    //   A_shifted = noseShift · tilt · [ T(bridge)·scale·T(-bridge) ] · A
    //
    // - scale: about the bridge point so the frame resizes in place.
    // - tilt (pantoscopic): rotate about X around the bridge so the BOTTOM of
    //   the lens tips toward the cheek, like real glasses resting on nose+ears.
    //   Real frames have ~8-12°. Without it, the frame reads too vertical and
    //   rides high on the brow when you look down.
    // - noseShift: nudge +Z onto the nose.
    const bx = 0.0, by = 3.271, bz = 5.236;  // canonical bridge (lm 168)
    const s = this._scale / this._headScale;

    // Scale about bridge.
    this._scaleMat.set(
      s, 0, 0, bx - s * bx,
      0, s, 0, by - s * by,
      0, 0, s, bz - s * bz,
      0, 0, 0, 1
    );

    // Pantoscopic tilt about X, pivoted at the bridge. Positive tilt rotates the
    // bottom of the lens toward the face. Build T(b)·Rx·T(-b).
    const a = (this._pantoTiltDeg || 0) * Math.PI / 180;
    const ca = Math.cos(a), sa = Math.sin(a);
    // Rx = [[1,0,0],[0,ca,-sa],[0,sa,ca]]; pivot at bridge (by,bz on Y,Z).
    this._tiltMat.set(
      1, 0,   0,    0,
      0, ca, -sa,   by - (ca*by - sa*bz),
      0, sa,  ca,   bz - (sa*by + ca*bz),
      0, 0,   0,    1
    );

    this._noseShift.makeTranslation(0, 0, this._noseShiftZ);
    // (when auto-seating is on, _noseShiftZ = autoShift + manual offset)

    // A_shifted = noseShift · tilt · scaleAboutBridge · A
    this._A_shifted.multiplyMatrices(this._scaleMat, this._A);
    this._A_shifted.premultiply(this._tiltMat);
    this._A_shifted.premultiply(this._noseShift);
  }

  setNoseDistance(cm) {
    // Panel/API sets the TOTAL forward shift; keep the manual offset consistent.
    this._noseShiftZ = cm;
    this._noseOffsetZ = cm - this._autoShiftZ;
    this._rebuildShift();
  }
  getAutoSeatShift() { return this._autoShiftZ; }

  /** Re-run the auto-seat solve (after the seating surface changed). */
  reseat() {
    if (!this._autoSeatOn || !this._model) return;
    this._noseShiftZ = 0; this._rebuildShift();
    this._autoShiftZ = this._solveAutoSeat(this._model);
    this._noseShiftZ = this._autoShiftZ + this._noseOffsetZ;
    this._rebuildShift();
  }

  /** Head scale carried by the pose matrix (true-to-scale mode). */
  setHeadScale(s) {
    if (!Number.isFinite(s) || Math.abs(s - this._headScale) < 1e-4) return;
    this._headScale = s;
    if (this._autoSeatOn && this._model) {
      this._noseShiftZ = 0; this._rebuildShift();
      this._autoShiftZ = this._solveAutoSeat(this._model);
      this._noseShiftZ = this._autoShiftZ + this._noseOffsetZ;
    }
    this._rebuildShift();
  }
  getNoseDistance() { return this._noseShiftZ; }
  hasNoseOverride() { return this._noseOverride !== null; }

  setScale(s) {
    this._scale = s;
    this._rebuildShift();
  }
  getScale() { return this._scale; }
  hasScaleOverride() { return this._scaleOverride !== null; }

  setPantoTilt(deg) {
    this._pantoTiltDeg = deg;
    this._rebuildShift();
  }
  getPantoTilt() { return this._pantoTiltDeg; }
  hasTiltOverride() { return this._tiltOverride !== null; }

  /**
   * @param {string} url
   * @param {number} [modelNoseDistance]  per-model forward shift (cm)
   * @param {number} [modelScale]         per-model scale multiplier
   *        Per-model values are ignored when the matching ?nose=/?scale=
   *        URL override is active.
   */
  async load(url, modelNoseDistance, modelScale, modelTilt) {
    if (this._model) {
      this.root.remove(this._model);
      this._dispose(this._model);
      this._model = null;
    }
    let rebuild = false;
    if (this._noseOverride === null && Number.isFinite(modelNoseDistance)) {
      this._noseOffsetZ = modelNoseDistance;
      this._noseShiftZ = modelNoseDistance; rebuild = true;
    }
    if (this._scaleOverride === null && Number.isFinite(modelScale)) {
      this._scale = modelScale; rebuild = true;
    }
    if (this._tiltOverride === null && Number.isFinite(modelTilt)) {
      this._pantoTiltDeg = modelTilt; rebuild = true;
    }
    if (rebuild) this._rebuildShift();

    const gltf = await this._loader.loadAsync(url);
    const model = gltf.scene;
    this._applyMaterials(model);

    // Compute the model's bridge point (front-centre-upper of bbox) in MODEL-
    // LOCAL space — BEFORE adding to this.root. setFromObject uses world space,
    // and this.root.matrix already holds the posed (~98×) matrix from prior
    // frames on a thumbnail switch, which would inflate the bbox ~98× and break
    // bridge-pinning (frame flung off-screen). Computing local, pre-add, is safe.
    model.updateWorldMatrix(true, true);
    const bb = new Box3().setFromObject(model);
    const sz = new Vector3(); bb.getSize(sz);
    this._modelBridge.set(
      (bb.min.x + bb.max.x) / 2,
      bb.max.y - sz.y * 0.30,
      bb.max.z
    );

    // Auto-seat: push the frame forward until its front clears the canonical
    // face surface (nose, cheeks, brow). Must run with the nose shift at 0 so
    // the solve is absolute, then the manual offset is added back on top.
    if (this._autoSeatOn) {
      this._noseShiftZ = 0; this._rebuildShift();
      this._autoShiftZ = this._solveAutoSeat(model);
      this._noseShiftZ = this._autoShiftZ + this._noseOffsetZ;
      this._rebuildShift();
    }

    // Keep pristine vertex positions so the temple splay can be re-applied.
    model.traverse((o) => { if (o.isMesh && o.geometry?.attributes?.position) o.userData.origPos = o.geometry.attributes.position.array.slice(); });
    this._frameStats = this._analyzeFrame(model);
    this._model = model;
    this.root.add(model);
    this.root.visible = false;
    if (this._splayOn && this._splayRatio !== 1) this._applySplay();

    console.log(`[Glasses] Loaded ${url} — nose ${this._noseShiftZ.toFixed(2)}cm, scale ${this._scale.toFixed(2)} (M·A), modelBridge ${this._modelBridge.x.toFixed(3)},${this._modelBridge.y.toFixed(3)},${this._modelBridge.z.toFixed(3)}`);
  }

  /**
   * Solve the forward (+Z canonical) shift that makes the frame FRONT clear the
   * canonical face surface. Samples the model's vertices (transformed by
   * A_shifted with nose shift 0), keeps only the front piece — everything within
   * FRONT_DEPTH cm of the front-most point, which covers rims, bridge and nose
   * pads but excludes the temple arms (which MUST go behind the face) — and
   * returns max(penetration) + clearance, clamped to a sane range.
   *
   * Why this matters: the live occluder's depth is M·canonicalVertex — the SAME
   * canonical surface sampled here. A front that clears this surface therefore
   * cannot be depth-culled by the occluder at any head angle. This replaces the
   * hand-tuned per-model noseDistance/occBias pairs that tried to fix nose
   * clipping empirically (and failed for some models).
   */
  _solveAutoSeat(model) {
    const FRONT_DEPTH = 2.2;   // cm behind the front-most point that still counts as "front"
    const CLEARANCE   = 0.15;  // cm of air between skin and frame
    const MAX_SHIFT   = 2.5;

    // Gather transformed vertices (stride to cap the sample count).
    const pts = [];
    const v = new Vector3();
    let frontZ = -Infinity;
    model.updateWorldMatrix(true, true);
    model.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry?.attributes?.position) return;
      const pos = obj.geometry.attributes.position;
      const stride = Math.max(1, Math.ceil(pos.count / 12000));
      for (let i = 0; i < pos.count; i += stride) {
        v.fromBufferAttribute(pos, i)
         .applyMatrix4(obj.matrixWorld)   // model-local → glasses-root local
         .applyMatrix4(this._A_shifted);  // → canonical face space (cm)
        pts.push(v.x, v.y, v.z);
        if (v.z > frontZ) frontZ = v.z;
      }
    });
    if (!pts.length) return 0;

    const front = [];
    for (let i = 0; i < pts.length; i += 3) {
      if (pts[i + 2] >= frontZ - FRONT_DEPTH) front.push(pts[i], pts[i + 1], pts[i + 2]);
    }
    const pen = maxPenetration(front);
    const shift = pen > 0 ? Math.min(MAX_SHIFT, pen + CLEARANCE) : 0;
    console.log(`[Glasses] auto-seat: ${front.length / 3} front verts, max penetration ${pen.toFixed(2)}cm → shift +${shift.toFixed(2)}cm`);
    return shift;
  }

  /** Frame extents in canonical space: front plane, outer half-width, temple tip. */
  _analyzeFrame(model) {
    const v = new Vector3();
    let frontZ = -Infinity, minZ = Infinity;
    const pts = [], ys = [];
    model.updateWorldMatrix(true, true);
    model.traverse((o) => {
      if (!o.isMesh || !o.geometry?.attributes?.position) return;
      const pos = o.geometry.attributes.position;
      const stride = Math.max(1, Math.ceil(pos.count / 8000));
      for (let i = 0; i < pos.count; i += stride) {
        v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld).applyMatrix4(this._A_shifted);
        pts.push(v.x, v.z); ys.push(v.y);
        if (v.z > frontZ) frontZ = v.z;
        if (v.z < minZ) minZ = v.z;
      }
    });
    let halfWidth = 0;
    for (let i = 0; i < pts.length; i += 2) if (pts[i + 1] >= frontZ - 2.2 && Math.abs(pts[i]) > halfWidth) halfWidth = Math.abs(pts[i]);

    // Hinge depth from the geometry, not a fixed distance: looking at the OUTER
    // vertices (|x| > 0.7·halfWidth) in 0.25 cm depth bins from the front
    // backwards, the rim / end-piece is tall (y extent > ~1.5 cm) while the
    // temple arm is a thin bar (< ~1.2 cm). The hinge is the first thin bin.
    // (A fixed 2 cm cut bent the outer rims of curved aviator lenses.)
    const BIN = 0.25;
    const nb = Math.max(1, Math.ceil((frontZ - minZ) / BIN) + 1);
    const yMin = new Float32Array(nb).fill(Infinity), yMax = new Float32Array(nb).fill(-Infinity);
    for (let i = 0; i < pts.length; i += 2) {
      if (Math.abs(pts[i]) < 0.7 * halfWidth) continue;
      const b = Math.min(nb - 1, Math.floor((frontZ - pts[i + 1]) / BIN));
      const y = ys[i / 2];
      if (y < yMin[b]) yMin[b] = y; if (y > yMax[b]) yMax[b] = y;
    }
    let hingeZ = frontZ - 2.0;   // fallback
    for (let b = 2; b < nb; b++) {            // skip the first 0.5 cm (front face)
      const ext = yMax[b] - yMin[b];
      if (Number.isFinite(ext) && ext < 1.2) { hingeZ = frontZ - b * BIN - 0.3; break; }
    }
    return { frontZ, halfWidth, minZ, hingeZ };
  }

  /**
   * Head-width ratio (user / canonical). Temple arms are rotated outward about a
   * hinge just behind the front so the temple TIPS end up `ratio` × wider —
   * like real temples flexing to a wider head. Applied as a one-time vertex
   * deformation in model space (pristine positions are kept for re-application).
   */
  setTempleSplay(ratio) {
    if (!Number.isFinite(ratio)) return;
    this._splayRatio = Math.max(0.85, Math.min(1.35, ratio));
    if (this._splayOn && this._model) this._applySplay();
  }
  getTempleSplay() { return this._splayRatio; }

  _applySplay() {
    const st = this._frameStats;
    if (!st || !this._model) return;
    const r = this._splayRatio;
    const zH = st.hingeZ;                       // hinge depth (from geometry)
    const L = Math.max(4, zH - st.minZ);        // temple length (cm)
    const xH = st.halfWidth;
    // tip should move from xH to xH·r (canonical-space cm): sinθ = xH(r−1)/L
    const theta = Math.asin(Math.max(-0.35, Math.min(0.35, xH * (r - 1) / L)));
    const cs = Math.cos(theta), sn = Math.sin(theta);
    const v = new Vector3();
    const toCanon = new Matrix4(), toModel = new Matrix4();
    this._model.updateWorldMatrix(true, true);
    this._model.traverse((o) => {
      if (!o.isMesh || !o.userData.origPos) return;
      const pos = o.geometry.attributes.position;
      const orig = o.userData.origPos;
      toCanon.multiplyMatrices(this._A_shifted, o.matrixWorld);
      toModel.copy(toCanon).invert();
      for (let i = 0; i < pos.count; i++) {
        v.set(orig[i*3], orig[i*3+1], orig[i*3+2]).applyMatrix4(toCanon);
        if (v.z < zH - 0.001 && Math.abs(v.x) > 0.75 * xH && theta !== 0) {
          // blend in over the first cm behind the hinge so the joint stays smooth
          const w = Math.min(1, (zH - v.z) / 1.0);
          const s = v.x >= 0 ? 1 : -1;
          const px = s * xH, dz = v.z - zH, dx = v.x - px;
          const nx = px + (dx * cs - dz * sn * s);
          const nz = zH + (dx * sn * s + dz * cs);
          v.x += (nx - v.x) * w; v.z += (nz - v.z) * w;
        }
        v.applyMatrix4(toModel);
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      pos.needsUpdate = true;
      o.geometry.computeVertexNormals?.();
      o.geometry.computeBoundingSphere();
    });
    console.log(`[Glasses] temple splay ratio ${r.toFixed(3)} → ${(theta * 180 / Math.PI).toFixed(1)}° per arm (hinge ±${xH.toFixed(1)} cm at z ${zH.toFixed(2)}, front ${st.frontZ.toFixed(2)}, L ${L.toFixed(1)} cm)`);
  }

  _applyMaterials(root) {
    const tokens = (this.lensSpec.meshTokens || ['lens', 'glass', 'clear']).map(t => t.toLowerCase());
    this._lensMats = [];
    this._frameMats = [];

    root.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.frustumCulled = false;
      const mn = (obj.name || '').toLowerCase();
      const matName = (obj.material?.name || '').toLowerCase();
      const isLens = tokens.some(t => mn.includes(t) || matName.includes(t));

      if (isLens) {
        // Tinted glass by plain alpha blending — NOT `transmission`. Three.js
        // transmission samples a render target holding only this scene's opaque
        // objects; the camera image is drawn in a separate background scene
        // (frame-lock), so a transmissive lens "sees" black behind it and every
        // lens came out near-opaque regardless of its colour. Alpha blending
        // composites over whatever is already in the framebuffer (the video).
        const qsOp = parseFloat(new URLSearchParams(window.location.search).get('lensopacity'));
        const opacity = Number.isFinite(qsOp) ? qsOp
          : (this.lensSpec.opacity ?? (Number.isFinite(this.lensSpec.transmission) ? 1 - this.lensSpec.transmission : 0.55));
        const lens = new MeshPhysicalMaterial({
          color: new Color(this.lensSpec.color || '#1a1a1a'),
          transmission: 0,
          ior: this.lensSpec.ior ?? 1.5,
          roughness: this.lensSpec.roughness ?? 0.06,
          reflectivity: this.lensSpec.reflectivity ?? 0.9,
          metalness: 0.0,
          envMap: this.envMap,
          envMapIntensity: this.lensSpec.envMapIntensity ?? 1.9,
          transparent: true,
          opacity: Math.max(0, Math.min(1, opacity)),
          depthWrite: false,     // a see-through lens must not hide the temple behind it
          polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
        });
        obj.renderOrder = 11;    // after the frame, so blending sees the frame + video
        obj.material?.dispose?.();
        obj.material = lens;
        this._lensMats.push(lens);
      } else if (obj.material) {
        // Keep frame material, add reflections.
        if (obj.material.isMeshStandardMaterial || obj.material.isMeshPhysicalMaterial) {
          obj.material.envMap = this.envMap;
          obj.material.envMapIntensity = 1.3;
          // NOTE: no polygonOffset on the frame. polygonOffset here pushed the
          // temple-arm depth and made the occluder slice through the middle of
          // the arms (transparent-middle artifact). The frame should occlude
          // naturally against the face mesh.
          obj.material.needsUpdate = true;
          this._frameMats.push(obj.material);
        }
      }
    });

    console.log(`[Glasses] materials — lens meshes: ${this._lensMats.length}, frame materials: ${this._frameMats.length}`);
    if (this._lensMats.length === 0) {
      console.warn('[Glasses] No lens mesh matched tokens', tokens, '— lens will not be tinted/transmissive.');
    }
  }

  /**
   * Pose the glasses.
   *
   * Default: object matrix = M · A_shifted.
   *
   * If a live bridge anchor is supplied, we PIN the frame's bridge to the user's
   * real nose-bridge landmark while keeping orientation + scale from M. This
   * removes the static, angle-only offset caused by (a) the canonical-average
   * bridge sitting at a different depth than the user's real bridge, and (b) any
   * Z nose-shift projecting sideways under yaw. Both turn a depth difference into
   * a lateral screen offset when the head is turned; pinning the bridge to the
   * live landmark eliminates them at every angle.
   *
   * Method: let p = A_shifted · modelBridge (the frame's bridge in canonical
   * space). Keep M's rotation+scale (RS); replace its translation t' so that
   * RS·p + t' = liveBridgeWorld. Then object.matrix = [RS | t'] · A_shifted.
   *
   * @param {THREE.Matrix4} matrixM
   * @param {THREE.Vector3} [liveBridgeWorld]  unprojected real bridge (cm). If
   *        omitted, falls back to plain M · A_shifted.
   */
  /**
   * Compute the bridge-pinned matrix M' (rotation+scale from M, translation
   * overridden so the frame bridge lands on the live bridge). Does NOT apply A.
   * Returned so the OCCLUDER can be posed by the SAME M' as the glasses, keeping
   * them in one depth frame (no clipping, consistent occlusion). Returns null if
   * no live bridge is given (caller should use plain M).
   *
   * @param {THREE.Matrix4} matrixM
   * @param {THREE.Vector3} liveBridgeWorld
   * @returns {THREE.Matrix4|null}
   */
  /**
   * World position where the frame's bridge lands under a given pose matrix.
   * = poseM · (A_shifted · modelBridge). For diagnostics.
   */
  getFrameBridgeWorld(poseM) {
    const p = this._p.copy(this._modelBridge).applyMatrix4(this._A_shifted).clone();
    return p.applyMatrix4(poseM);
  }

  setPinPoint(v) { this._pin.copy(v); }

  computePinnedMatrix(matrixM, liveBridgeWorld) {
    if (!liveBridgeWorld) return null;
    // p = canonical skin bridge (see _pin). Frame front stays auto-seat distance ahead.
    this._p.copy(this._pin);
    // RS = M with translation zeroed.
    this._Mrs.copy(matrixM);
    this._Mrs.elements[12] = 0; this._Mrs.elements[13] = 0; this._Mrs.elements[14] = 0;
    // t' = liveBridge - RS·p
    this._rsP.copy(this._p).applyMatrix4(this._Mrs);
    this._Mpinned.copy(this._Mrs);
    this._Mpinned.elements[12] = liveBridgeWorld.x - this._rsP.x;
    this._Mpinned.elements[13] = liveBridgeWorld.y - this._rsP.y;
    this._Mpinned.elements[14] = liveBridgeWorld.z - this._rsP.z;
    return this._Mpinned;
  }

  /**
   * Pose the glasses. object.matrix = Mpose · A_shifted, where Mpose is either
   * the bridge-pinned matrix (if liveBridgeWorld given) or plain M.
   *
   * @param {THREE.Matrix4} matrixM
   * @param {THREE.Vector3} [liveBridgeWorld]
   */
  update(matrixM, liveBridgeWorld) {
    const pinned = this.computePinnedMatrix(matrixM, liveBridgeWorld);
    this._MA.multiplyMatrices(pinned || matrixM, this._A_shifted);
    this.root.matrix.copy(this._MA);
    this.root.visible = true;
  }

  setLensIntensity(v) { this._lensMats.forEach(m => { m.envMapIntensity = v; m.needsUpdate = true; }); }
  setLensOpacity(v) { this._lensMats.forEach(m => { m.opacity = Math.max(0, Math.min(1, v)); }); }
  getLensOpacity() { return this._lensMats[0]?.opacity ?? 0.55; }
  setFrameIntensity(v) { this._frameMats.forEach(m => { m.envMapIntensity = v; m.needsUpdate = true; }); }
  hide() { this.root.visible = false; }

  _dispose(obj) {
    obj.traverse((c) => {
      if (c.geometry) c.geometry.dispose();
      if (c.material) (Array.isArray(c.material) ? c.material : [c.material]).forEach(m => m.dispose());
    });
  }
  dispose() { if (this._model) this._dispose(this._model); }
}
